<template>
  <div class="gallery-grid-container">
    <ul
      class="gallery-grid gallery-grid-more-1 gallery-grid-more-2"
      :class="{
        'has-light-box': lightBoxReady,
        'd-block': onlyOneImage,
      }"
    >
      <li>
        <button
          v-if="sliderImages.length"
          class="card-radius-top gallery-slider-wrap "
          type="button"
          tabindex="0"
          :data-ident="`gallerySliderWrap`"
          @click="$emit('open-gallery', 0)"
        >
          <gallery-slider-custom
            :key="objectNumber + '_slider'"
            :gallery-link="$route"
            :images-arr="sliderImages"
            :wrapper="'gallery gallery-object gallery-object-page'"
            :image-type="'objectGridBig'"
            :object-number="objectNumber"
            :enable-pinch="isMobileDevice"
            :enable-counter="false"
            :show-controls="isMobileDevice && !onlyOneImage"
            @pinch="openGallery"
          />
        </button>
      </li>
      <li
        v-for="(image, index) in secondaryImages"
        :key="image.no"
      >
        <button
          tabindex="0"
          type="button"
          class="btn btn-image"
          :data-ident="`secondaryImages${index}`"
          @click="$emit('open-gallery', index + 1)"
        >
          <lazy-photo
            :img-src="image.src"
            :img-title="image.description"
            :img-type="'objectGrid'"
            :img-no="image.no"
            :img-cdn="image.cdn"
            :preview-size="'320x210'"
            :object-number="getActiveObjectNumber"
            :extra-class-wrapper="'gallery-grid-photo-wrapper d-flex'"
          />
        </button>
        <div
          v-if="lightBoxReady"
          class="options"
        >
          <div
            v-if="imagesArr.length < 8"
            v-b-tooltip.hover.noninteractive
            :title="$t('common:images.tooltipImage', {showMoreImages:showMoreImages})"
            class="more"
          >
            <i class="icon"><icon-search /></i>
            {{ $t('common:images.showMoreImages', {showMoreImages:showMoreImages}) }}
          </div>
        </div>
      </li>
      <li v-if="subImages.length">
        <ul>
          <li
            v-for="(image, index) in subImages"
            :key="image.nr"
          >
            <button
              tabindex="0"
              class="btn btn-image"
              type="button"
              :data-ident="`objectGridSub${index}`"
              @click="$emit('open-gallery', index + 3)"
            >
              <lazy-photo
                :img-src="image.src"
                :img-title="image.description"
                :img-type="'objectGridSub'"
                :img-no="image.no"
                :img-cdn="image.cdn"
                :img-blur="index === subImages.length - 1"
                :preview-size="'250x155'"
                :object-number="getActiveObjectNumber"
              />
            </button>
            <button
              v-if="lightBoxReady"
              type="button"
              class="options"
              :data-ident="`showMoreImages`"
              @click="$emit('open-gallery', index + 3)"
            >
              <i class="icon"><icon-search /></i>
              {{ $t('common:images.showMoreImages',{showMoreImages:showMoreImages}) }}
            </button>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>
<script>
import LazyPhoto from 'components/LazyPhoto.vue';
import { mapGetters } from 'vuex';
import DeviceHelper from 'mixins/DeviceHelper';

/* ICONS */
import IconSearch from 'ICON:IconSearch';

import { VBTooltip } from 'bootstrap-vue';
import 'scss/bootstrap/vue/components/tooltip.scss';

export default {
  name: 'GalleryGrid',
  components: {
    GallerySliderCustom: () => import(/* webpackChunkName: "gallery-slider-custom" */ 'components/GallerySliderCustom.vue'),
    LazyPhoto,
    IconSearch,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [DeviceHelper],
  props: {
    imagesArr: {
      type: Array,
      default: () => [],
    },
    lightBoxReady: {
      type: Boolean,
      default: false,
    },
    objectNumber: {
      type: [Number, String],
      required: false,
      default: 0,
    },
  },
  computed: {
    ...mapGetters(['getActiveObjectNumber']),
    // on desktop only main image, no sliding
    // on mobile slide through all images
    sliderImages() {
      if (this.isMobileDevice) {
        return this.imagesArr;
      }
      return this.imagesArr.slice(0, 1);
    },
    secondaryImages() {
      return this.imagesArr.slice(1, 3);
    },
    subImages() {
      if (this.imagesArr.length >= 8) {
        return this.imagesArr.slice(3, 8);
      }
      return [];
    },
    showMoreImages() {
      return this.imagesArr.length;
    },
    onlyOneImage() {
      // do not use grid if only one image is available
      return this.imagesArr.length === 1;
    },
    isMobileDevice() {
      return ['xs'].includes(this.dh_breakpoint);
    },
  },
  methods: {
    openGallery(indexParam) {
      this.$emit('open-gallery', indexParam);
    },
  },
};
</script>

<style lang="scss" scoped>
  .has-light-box li {
    cursor: pointer;
  }

  ul.gallery-grid {
    margin: 0;
    padding: 0;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: auto auto auto;
    grid-gap: 10px;
    overflow: hidden;

    @media #{$media_xs} {
      display: block;
    }

    .gallery-slider-wrap {
      padding-left:0;
      padding-top: 66%;
      .gallery-object-page {
        position: absolute;
        top: 0;
        height: 100%;
      }
    }

    > li {
      list-style: none;
      margin: 0;
      display: block;
      height: 0;
      padding: 0 0 66.6%;
      position: relative;
      overflow: hidden;

      @include radius($radius_card);

      @media #{$media_xs} {
        @include radius(0);
      }

      a {
        display: block;
      }

      .options {
        display: none;
        position: absolute;
        z-index: 10;
        bottom: 0;
        right: 0;
        height: 100%;
        width: 100%;
        background: rgba(#000, 0.2);

        @include font_bold;

        color: #fff;

        @include text_shadow_strong;

        @media #{$media_sm} {
          font-size: $font_size_s;
        }

        @media #{$media_xxl} {
          font-size: $font_size_l;
        }

        @media #{$media_xs} {
          font-size: $font_size_m;
          display: block;
          background: none;
          right: $margin_content_xs;
          bottom: $margin_content_xs;
        }

        .icon {
          @include drop_shadow_strong;
        }

        .more {
          position: absolute;
          display: block;
          text-align: center;
          width: 100%;
          top: 50%;
          margin: -0.5em 0 0;
          line-height: 1em;

          @media #{$media_xs} {
            top: auto;
            width: auto;
            height: auto;
            margin: 0;
            right: 0;
            bottom: 0;
          }
        }
      }

      ul {
        padding: 0;
        margin: 0;
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
        grid-template-rows: auto;
        grid-gap: 10px;

        @media #{$media_sm} {
          grid-template-columns: repeat(auto-fit, minmax(99px, 1fr));
        }

        @media #{$media_xs} {
          display: none;
        }

        li {
          display: block;
          margin: 0;
          padding: 0;
          list-style: none;
          position: relative;
          overflow: hidden;

          @include radius($radius_card);

          @media #{$media_xs} {
            @include radius(0);
          }
        }
      }

      img {
        width: 100%;
        height: auto;
      }

      &:nth-child(1) {
        grid-column-start: 2;
        grid-column-end: 4;
        grid-row-start: 1;
        grid-row-end: 3;
        padding: 0 0 66%;
      }

      &:nth-child(2) {
        grid-column-start: 1;
        grid-column-end: 2;
        grid-row-start: 1;
        grid-row-end: 2;
        padding: 0 0 65.5%;

        @media #{$media_xs} {
          display: none;
        }
      }

      &:nth-child(3) {
        grid-column-start: 1;
        grid-column-end: 2;
        grid-row-start: 2;
        grid-row-end: 3;
        padding: 0 0 65.5%;

        @media #{$media_xs} {
          display: none;
        }
      }

      &:nth-child(4) {
        grid-column-start: 1;
        grid-column-end: 4;
        grid-row-start: 3;
        grid-row-end: 4;
        padding: 0;
        height: auto;

        ul {
          li {
            @media #{$media_xxl} {
              height: 116px;
            }

            @media #{$media_xl} {
              height: 94px;
            }

            @media #{$media_lg} {
              height: 74px;
            }

            @media #{$media_md} {
              height: 85px;
            }

            @media #{$media_sm} {
              // height: 79px;
              height: 62px;
            }
          }
        }

        @media #{$media_xs} {
          display: none;
        }
      }
    }

    &.gallery-grid-more-1 {
      > li {
        &:nth-child(1) {
          .options {
            display: block;
            background: none;
            width: auto;
            height: auto;
            right: $margin_content;
            bottom: $margin_content;

            .more {
              margin: 0;
              position: relative;
            }
          }
        }
      }
    }

    &.gallery-grid-more-2 {
      > li {
        &:last-child {
          ul {
            li:last-child {
              .options {
                display: block;
              }
            }
          }
        }
      }
    }

    .browserIE & {
      margin: 0 -2% 0 0;
      overflow: hidden;
      display: block;
      width: 100%;

      > li {
        float: right;
        width: 33%;
        margin: 0 2% 2% 0;
        padding: 0;
        overflow: hidden;
        height: auto;
        display: block;
        height: 0;
        padding: 0 0 21.5%;

        &:first-child {
          width: 63%;
          padding: 0 0 45%;
        }

        &:nth-child(4) {
          float: none;
          width: 100%;
          height: auto;
          padding: 0;

          @media #{$media_md} {
            display: none;
          }

          ul {
            li {
              width: 18%;
              margin: 0 2% 0 0;
              float: left;
              display: block;
              height: 0;
              padding: 0 0 11%;
            }
          }
        }
      }
    }
  }
</style>
